import { useState } from 'react';
import { cn } from '@/lib/utils';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import type { FacetasDeLaBandeja } from '../types/price-proposals.types';

type FacetaDeEstrategia = NonNullable<FacetasDeLaBandeja['estrategias']>[number];

/**
 * El color de cada estrategia en Revisar precios (la maqueta: un punto al lado
 * del nombre, el mismo en su chip y en sus filas). Sale del orden de las
 * facetas, así que se mantiene mientras se mira la bandeja. Son los colores de
 * Prixia y los semánticos que no dicen «bien» ni «mal».
 */
const COLORES = [
  'bg-primary',
  // El celeste y el dorado de la marca: los de la app (el tema de Compite no
  // cambia colores desde el 15-oct).
  'bg-prixia-sky',
  'bg-prixia-gold',
  'bg-ia',
  'bg-info',
  'bg-foreground/70',
] as const;

/** Sin estrategia (un recálculo de reglas): gris, no compite con los colores. */
export const COLOR_SIN_ESTRATEGIA = 'bg-muted-foreground/50';

export function colorDeEstrategia(
  id: string | null | undefined,
  estrategias: FacetaDeEstrategia[] | null | undefined
): string {
  if (!id) return COLOR_SIN_ESTRATEGIA;
  const i = (estrategias ?? []).findIndex((e) => e.valor === id);
  return i < 0 ? COLOR_SIN_ESTRATEGIA : COLORES[i % COLORES.length];
}

/** Cuántas se ven antes de «+N más»: la maqueta muestra tres, con aire. */
const VISIBLES = 5;

/**
 * El filtro por estrategia de la maqueta: «Todas 28 · Sostener margen 27% 10 ·
 * …», en píldoras, cada una con su punto de color y cuántas tiene en esta
 * pestaña (sin el filtro de estrategia, spec 275 R1-c). Una terminada lo dice.
 *
 * La elegida que no está en las facetas (se llegó desde Decisiones y en esta
 * pestaña tiene 0) igual se muestra, elegida, con su nombre guardado y su 0:
 * si no, no habría cómo ver qué filtra ni sacarlo (ola final R1).
 */
export function ChipsDeEstrategia({
  estrategias,
  elegida,
  nombreDeLaElegida,
  totalDeTodas,
  onElegir,
  className,
}: {
  estrategias: FacetaDeEstrategia[];
  elegida: string | null;
  /** El nombre de la elegida cuando no está en las facetas. */
  nombreDeLaElegida: string | null;
  /** Cuántas hay con todas las estrategias; null = no se sabe (no se muestra). */
  totalDeTodas: number | null;
  onElegir: (id: string | null, nombre: string | null) => void;
  className?: string;
}) {
  const [todas, setTodas] = useState(false);

  const huerfana: FacetaDeEstrategia | null =
    elegida && !estrategias.some((e) => e.valor === elegida)
      ? {
          valor: elegida,
          nombre: nombreDeLaElegida ?? 'La elegida',
          terminada: false,
          cuantas: 0,
        }
      : null;
  const lista = huerfana ? [...estrategias, huerfana] : estrategias;
  const sobran = Math.max(0, lista.length - VISIBLES);
  // La elegida siempre a la vista, aunque esté más allá de las primeras.
  const visibles = todas ? lista : lista.filter((e, i) => i < VISIBLES || e.valor === elegida);
  const escondidas = lista.length - visibles.length;

  return (
    <div role="group" aria-label="Estrategia" className={cn('flex flex-wrap gap-1.5', className)}>
      <Chip
        activa={elegida === null}
        onClick={() => onElegir(null, null)}
        texto="Todas"
        cuenta={elegida === null ? totalDeTodas : null}
      />
      {visibles.map((e) => {
        const titulo = `${e.nombre}${e.terminada ? ' (terminada)' : ''} (${formatoCantidad(e.cuantas)})`;
        return (
          <Chip
            key={e.valor}
            activa={e.valor === elegida}
            onClick={() => onElegir(e.valor, e.nombre)}
            texto={e.nombre}
            titulo={titulo}
            punto={colorDeEstrategia(e.valor, estrategias)}
            terminada={e.terminada}
            cuenta={e.cuantas}
          />
        );
      })}
      {sobran > 0 && (escondidas > 0 || todas) && (
        <button
          type="button"
          aria-expanded={todas}
          onClick={() => setTodas((t) => !t)}
          className="inline-flex h-8 items-center rounded-full px-3 text-xs font-semibold text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {todas ? 'Ver menos' : `+${formatoCantidad(escondidas)} más`}
        </button>
      )}
    </div>
  );
}

function Chip({
  activa,
  onClick,
  texto,
  titulo,
  punto,
  terminada = false,
  cuenta,
}: {
  activa: boolean;
  onClick: () => void;
  texto: string;
  titulo?: string;
  punto?: string;
  terminada?: boolean;
  cuenta: number | null;
}) {
  return (
    <button
      type="button"
      aria-pressed={activa}
      title={titulo}
      onClick={onClick}
      className={cn(
        // Como la maqueta: chicas (30 px, letra de 12), la elegida en tinta llena.
        'inline-flex h-8 max-w-full items-center gap-1.5 rounded-full border px-3 text-xs transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        activa
          ? 'border-foreground bg-foreground font-bold text-background'
          : 'border-border bg-card font-semibold text-foreground/80 hover:bg-muted'
      )}
    >
      {/* El punto de color va en las filas (la maqueta no lo pone en la píldora). */}
      {punto && activa && (
        <span aria-hidden="true" className={cn('h-1.5 w-1.5 shrink-0 rounded-full', punto)} />
      )}
      {/* Ronda de arreglos: un nombre largo se corta; entero en el `title`. */}
      <span className="min-w-0 max-w-[12rem] truncate">{texto}</span>
      {terminada && ' '}
      {terminada && (
        <span
          className={cn(
            'rounded-full px-1.5 text-xs font-medium',
            activa ? 'bg-background/20 text-background' : 'bg-muted text-muted-foreground'
          )}
        >
          terminada
        </span>
      )}
      {cuenta != null && ' '}
      {cuenta != null && (
        <span
          className={cn(
            'font-medium tabular-nums',
            activa ? 'text-background/75' : 'text-muted-foreground'
          )}
        >
          {formatoCantidad(cuenta)}
        </span>
      )}
    </button>
  );
}
